import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import {
  IconUpload,
  IconAdjustments,
  IconCheck,
  IconFileText,
  IconTrash,
} from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { FormField } from '@/components/forms/FormField'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { toast } from '@/components/toasts/toast.helpers'

import {
  CriticalBanner,
  InfoBanner,
  KnowledgeCard,
  KnowledgeDocumentsUploader,
  handleApiError,
} from './AiAnswersKnowledgeActions'
import {
  deleteBaltoKnowledgeDocument,
  fetchBaltoKnowledgeDocuments,
  fetchBaltoProductFeatures,
  uploadBaltoKnowledgeDocument,
} from './baltoKnowledge.api'

// Sentinel for the picker only. Selecting it stores no feature tags, which is
// how the API represents "applies to every organization" — but requiring an
// explicit choice stops someone reaching that state by forgetting to tag.
export const ALL_FEATURES = '__all_features__'

const uploadValidationSchema = Yup.object().shape({
  features: Yup.array().min(1, 'Choose at least one feature, or "All features"').required(),
  files: Yup.array().min(1).required(),
})

// The API stores "applies to everyone" as an empty tag list.
export const toApiFeatures = (selected = []) => (selected.includes(ALL_FEATURES) ? [] : selected)

export const describeFeatures = (featureKeys, labelsByKey) => {
  if (isEmpty(featureKeys)) {
    return 'All features'
  }
  return featureKeys.map((key) => labelsByKey[key] ?? key).join(', ')
}

const useProductFeatures = () => {
  const dispatch = useDispatch()
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const features = await dispatch(fetchBaltoProductFeatures())
        if (!cancelled) {
          setOptions((features ?? []).map(({ key, label }) => ({ value: key, label, key })))
        }
      } catch (err) {
        if (!cancelled) {
          // Held in state, not just toasted: a failed fetch leaves the picker
          // showing only the synthetic "All features" option, which reads as
          // "no features exist" - and by the time anyone looks at it the toast
          // is gone.
          handleApiError(
            'Fetch product features failed:',
            'Unable to load the feature list. Please try again.',
            setError,
            err
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const labelsByKey = Object.fromEntries(options.map(({ value, label }) => [value, label]))

  return { options, loading, labelsByKey, error }
}

const CentralKnowledgeBanner = () => (
  <InfoBanner
    header="Balto central knowledge base"
    message={
      'Documents about how Balto works, available to every organization. Agents see a ' +
      'document only if their organization has one of the features it is tagged with. ' +
      'Nothing here is customer-specific.'
    }
  />
)

const FeatureSelect = ({ value, options, loading, disabled, onChange }) => {
  // "All features" is mutually exclusive with the specific ones, so the two can
  // never disagree about who the document is for.
  const handleChange = (selected) => {
    const values = selected?.map((option) => option.value) ?? []
    const justPickedAll = values.includes(ALL_FEATURES) && !value.includes(ALL_FEATURES)
    onChange(justPickedAll ? [ALL_FEATURES] : values.filter((value) => value !== ALL_FEATURES))
  }

  const allOptions = [{ value: ALL_FEATURES, label: 'All features', key: ALL_FEATURES }, ...options]

  return (
    <FormField className="half-width" data-testid="balto-knowledge-features">
      <label>Applies to features</label>
      <MultiSelect
        placeholder="Select features"
        loading={loading}
        options={allOptions}
        value={allOptions.filter((option) => value.includes(option.value))}
        onChange={handleChange}
        disabled={disabled}
        fixedWidth
      />
      <p className="muted-text">
        Choose the product features this document describes. Agents only see it if their
        organization has one of them. The list is maintained by hand &mdash; if the feature you need
        isn&apos;t here, reach out to the RTG3 team to have it added.
      </p>
    </FormField>
  )
}

export const UploadBaltoKnowledge = ({ onBack }) => {
  const dispatch = useDispatch()
  const { options, loading, labelsByKey, error: featuresError } = useProductFeatures()

  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploadError, setUploadError] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const uploadSuccess = !isEmpty(uploadedFiles)

  const handleSubmit = async (values, { setSubmitting, setFieldValue }) => {
    setIsUploading(true)
    setUploadedFiles([])
    setUploadError(null)

    const features = toApiFeatures(values.features)
    let uploaded = []

    try {
      for (const file of values.files) {
        const data = await dispatch(uploadBaltoKnowledgeDocument({ file, features }))
        if (Array.isArray(data?.uploaded_files)) {
          uploaded = uploaded.concat(data.uploaded_files)
        }
      }

      setUploadedFiles(uploaded)
      setFieldValue('files', [])
      toast.success(
        `Uploaded ${uploaded.length} document${uploaded.length === 1 ? '' : 's'} to the ` +
          'Balto knowledge base'
      )
    } catch (err) {
      handleApiError(
        'Balto knowledge upload failed:',
        'There was a problem while uploading your document(s). Please try again.',
        setUploadError,
        err
      )
    } finally {
      setSubmitting(false)
      setIsUploading(false)
    }
  }

  return (
    <KnowledgeCard
      title="Upload to the central knowledge base"
      subtitle="Documentation about Balto, shared across every organization"
      icon={IconUpload}
      iconClassName="ai-answers-upload"
      testId="balto-knowledge-upload"
    >
      <CentralKnowledgeBanner />

      {featuresError && <CriticalBanner header="Could not load features" message={featuresError} />}

      {uploadError && <CriticalBanner header="Upload failed" message={uploadError} />}

      <Formik
        initialValues={{ features: [], files: [] }}
        validationSchema={uploadValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, submitForm, setFieldValue, setSubmitting }) => {
          const isBusy = isSubmitting || isUploading
          // Editing either field starts a new upload, so the previous result
          // stops describing what is on screen and is cleared. Only the upload
          // in flight locks the form - locking on success too left no way to
          // tag a second document without leaving and coming back.
          const startOver = () => {
            setUploadedFiles([])
            setUploadError(null)
          }

          return (
            <>
              <FeatureSelect
                value={values.features}
                options={options}
                loading={loading}
                disabled={isBusy}
                onChange={(next) => {
                  startOver()
                  setFieldValue('features', next)
                }}
              />

              <hr className="mb" />

              <p className="muted-text">
                The file name is shown to agents as the source of any answer it produces, and to
                managers on the AI Answers page — name documents the way you would want a customer
                to read them.
              </p>

              <KnowledgeDocumentsUploader
                allowEdits={!isBusy}
                values={values}
                setFieldValue={(field, value) => {
                  if (field === 'files') {
                    startOver()
                  }

                  setFieldValue(field, value)
                }}
                setSubmitting={setSubmitting}
              />

              {uploadSuccess && (
                <div className="flex-align-center small-gap" data-testid="balto-upload-success">
                  <IconCheck size={18} />
                  <span>
                    Uploaded {uploadedFiles.length} document
                    {uploadedFiles.length === 1 ? '' : 's'} —{' '}
                    {describeFeatures(toApiFeatures(values.features), labelsByKey)}
                  </span>
                </div>
              )}

              <div className="flex-align-center gap">
                <Button type="button" secondary onClick={onBack} disabled={isBusy}>
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={submitForm}
                  disabled={isBusy || isEmpty(values.files)}
                  loading={isBusy}
                  data-testid="balto-knowledge-upload-submit"
                >
                  Upload
                </Button>
              </div>
            </>
          )
        }}
      </Formik>
    </KnowledgeCard>
  )
}

export const ManageBaltoKnowledge = ({ onBack }) => {
  const dispatch = useDispatch()
  const { labelsByKey } = useProductFeatures()

  const [documents, setDocuments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [deletingPath, setDeletingPath] = useState(null)

  const load = async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      setDocuments((await dispatch(fetchBaltoKnowledgeDocuments())) ?? [])
    } catch (err) {
      handleApiError(
        'Fetch Balto knowledge failed:',
        'Unable to load documents. Please try again.',
        setLoadError,
        err
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleDelete = async (filePath) => {
    setDeletingPath(filePath)
    try {
      await dispatch(deleteBaltoKnowledgeDocument({ filePath }))
      toast.success('Document deleted')
      await load()
    } catch (err) {
      handleApiError(
        'Delete Balto knowledge failed:',
        'Unable to delete the document. Please try again.',
        null,
        err
      )
    } finally {
      setDeletingPath(null)
    }
  }

  const rows = documents.map(({ file_path: filePath, features }) => ({
    // AdvancedTable keys rows by `index`, which defaults to id.
    id: filePath,
    document: filePath,
    features: describeFeatures(features, labelsByKey),
    actions: (
      <Button
        type="button"
        secondary
        onClick={() => handleDelete(filePath)}
        disabled={Boolean(deletingPath)}
        loading={deletingPath === filePath}
        data-testid={`balto-knowledge-delete-${filePath}`}
      >
        <IconTrash />
      </Button>
    ),
  }))

  return (
    <KnowledgeCard
      title="Manage the central knowledge base"
      subtitle="Documentation about Balto, shared across every organization"
      icon={IconAdjustments}
      iconClassName="ai-answers-search-or-delete"
      testId="balto-knowledge-manage"
    >
      <CentralKnowledgeBanner />

      {loadError && <CriticalBanner header="Could not load documents" message={loadError} />}

      {isLoading ? (
        <Loader />
      ) : !isEmpty(rows) ? (
        <AdvancedTable
          columns={[
            { accessor: 'document', label: 'Document' },
            { accessor: 'features', label: 'Applies to' },
            { accessor: 'actions', label: '', isSortable: false },
          ]}
          rows={rows}
          defaultOrderBy="document"
          testid="balto-knowledge-table"
        />
      ) : (
        // Only when the load actually succeeded: showing "no documents yet"
        // after a failed fetch would read as an empty knowledge base.
        !loadError && (
          <div
            className="flex-align-center small-gap muted-text"
            data-testid="balto-knowledge-empty"
          >
            <IconFileText size={18} />
            <span>
              No documents yet. Upload documentation about Balto features that agents might ask
              about — how mid-call playbook switching works, what QA scores mean.
            </span>
          </div>
        )
      )}

      <div className="flex-align-center gap">
        <Button type="button" secondary onClick={onBack}>
          Back
        </Button>
      </div>
    </KnowledgeCard>
  )
}
